import React, { useState } from 'react';
import { X, Sparkles, TrendingUp, AlertTriangle, DollarSign, Building, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import API_BASE_URL from '../config/api';

const InvestorAICoach = ({ isOpen, onClose, propertyData, metrics }) => {
  const { getToken, isLoaded } = useClerkAuth();
  const [analysis, setAnalysis] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const generateAnalysis = async () => {
    if (isAnalyzing) return;
    
    setIsAnalyzing(true);
    setAnalysis('');
    
    try {
      // Wait for Clerk to be loaded
      if (!isLoaded) {
        throw new Error('Authentication is still loading. Please wait a moment and try again.');
      }

      // Get fresh Clerk token with retry logic
      let token = null;
      let attempts = 0;
      const maxAttempts = 3;
      
      while (!token && attempts < maxAttempts) {
        try {
          token = await getToken();
          if (!token) {
            attempts++;
            if (attempts < maxAttempts) {
              console.log(`[InvestorAICoach] Token fetch attempt ${attempts} failed, retrying...`);
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        } catch (tokenError) {
          console.error('[InvestorAICoach] Token fetch error:', tokenError);
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      if (!token) {
        throw new Error('Unable to get authentication token. Please try logging out and back in.');
      }

      // Prepare investment data
      const purchasePrice = parseFloat(String(propertyData?.purchasePrice || '0').replace(/,/g, ''));
      const monthlyRent = parseFloat(String(propertyData?.monthlyRent || '0').replace(/,/g, ''));
      const annualRent = monthlyRent * 12;
      
      // Build the custom prompt for real estate investor analysis
      const investorPrompt = `You are an expert real estate investment analyst. Analyze this investment property deal and provide a comprehensive analysis in plain English that any investor can understand.

PROPERTY DETAILS:
- Purchase Price: $${purchasePrice.toLocaleString()}
- Monthly Rent: $${monthlyRent.toLocaleString()} (Annual: $${annualRent.toLocaleString()})
- Property Type: ${propertyData?.propertyType || 'Residential'}
- Down Payment: $${(metrics?.downPayment || 0).toLocaleString()} (${((metrics?.downPayment || 0) / purchasePrice * 100).toFixed(1)}%)
- Loan Amount: $${(metrics?.loanAmount || 0).toLocaleString()}
- Interest Rate: ${propertyData?.interestRate || 7}%
- Vacancy Rate: ${propertyData?.vacancyRate || 5}%

KEY METRICS:
- Cap Rate: ${(metrics?.capRate || 0).toFixed(2)}%
- Cash on Cash Return: ${(metrics?.cashOnCashReturn || 0).toFixed(2)}%
- NOI (Net Operating Income): $${(metrics?.noi || 0).toLocaleString()}
- Monthly Cash Flow: $${(metrics?.monthlyCashFlow || 0).toLocaleString()}
- Annual Cash Flow: $${(metrics?.annualCashFlow || 0).toLocaleString()}
- Operating Expenses: $${(metrics?.operatingExpenses || 0).toLocaleString()}/year
- Monthly Mortgage Payment: $${(metrics?.monthlyMortgage || 0).toLocaleString()}

INSTRUCTIONS FOR YOUR ANALYSIS:
1. Write in plain English - avoid jargon where possible
2. For EVERY acronym or technical term you use (like CAP rate, NOI, LTV, CoC, etc.), explain what it means in parentheses
3. Compare each key metric to INDUSTRY STANDARDS. Use these benchmarks:
   - Cap Rate: Industry standard is 5-10%, with 8% being a common target for residential
   - Cash on Cash Return: 8-12% is considered good, 12%+ is excellent
   - Debt Service Coverage Ratio: 1.25+ is considered safe
   - Expense Ratio: 35-50% of gross income is typical
   - Vacancy Rate: 5-8% is standard assumption
4. Provide specific, actionable recommendations
5. Be honest about both strengths AND weaknesses of this deal
6. End with a clear verdict: Is this a good investment? Why or why not?

FORMAT YOUR RESPONSE EXACTLY LIKE THIS:

DEAL OVERVIEW
[2-3 sentences summarizing the investment opportunity]

KEY METRICS ANALYSIS

Cap Rate: [X]%
The Cap Rate (Capitalization Rate) measures the property's return based on its income, calculated as NOI divided by purchase price. Industry standard: 5-10%. Your rate of [X]% is [above/below/within] this range, indicating [interpretation].

Cash on Cash Return: [X]%
Cash on Cash Return measures your actual cash return on the cash you invested. Industry standard: 8-12% is good, 12%+ is excellent. Your rate of [X]% is [interpretation].

[Continue for other relevant metrics...]

STRENGTHS
- [Bullet points of what's good about this deal]

CONCERNS
- [Bullet points of potential issues or risks]

RECOMMENDATIONS
1. [Specific actionable recommendation]
2. [Another recommendation]
3. [Another recommendation]

VERDICT
[Clear statement on whether this is a good investment and why]

---
IMPORTANT DISCLAIMER: This analysis is generated by AI for educational purposes only. It is not financial, investment, legal, or tax advice. All insights, projections, and recommendations should be independently verified with qualified professionals before making any investment decisions. Real estate investments carry risk, and past performance does not guarantee future results.`;

      console.log('[InvestorAICoach] Token obtained, calling AI Coach...');
      
      // Call the AI Coach API
      const response = await fetch(`${API_BASE_URL}/api/ai-coach/generate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          context: 'custom_investor_analysis',
          custom_prompt: investorPrompt,
          stream: false
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('[InvestorAICoach] AI Coach response received:', data);
      
      // Handle the response - check for the text in the expected field
      if (data.response) {
        setAnalysis(data.response);
      } else if (data.analysis) {
        setAnalysis(data.analysis);
      } else if (data.summary) {
        // For investor analysis, the response might come back formatted differently
        setAnalysis(data.summary);
      } else {
        setAnalysis('Unable to generate analysis. Please try again.');
      }
      
    } catch (error) {
      console.error('[InvestorAICoach] AI Coach error:', error);
      setAnalysis(`Unable to generate analysis: ${error.message}. Please ensure you are logged in and try again.`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (!isOpen) return null;

  // Calculate display values
  const purchasePrice = parseFloat(String(propertyData?.purchasePrice || '0').replace(/,/g, ''));
  const monthlyRent = parseFloat(String(propertyData?.monthlyRent || '0').replace(/,/g, ''));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-hidden bg-white shadow-2xl">
        {/* Header */}
        <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center space-x-2 text-xl">
                <Sparkles className="w-6 h-6" />
                <span>Fairy AI Coach</span>
              </CardTitle>
              <CardDescription className="text-purple-100 mt-1">
                Investment Deal Analysis
              </CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20 -mr-2 -mt-2"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] bg-white">
          {/* Deal Summary Cards */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <div className="flex items-center space-x-2 text-blue-700 mb-1">
                <Building className="w-4 h-4" />
                <span className="font-medium text-sm">Purchase Price</span>
              </div>
              <div className="text-2xl font-bold text-blue-800">
                ${purchasePrice.toLocaleString()}
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <div className="flex items-center space-x-2 text-green-700 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="font-medium text-sm">Monthly Rent</span>
              </div>
              <div className="text-2xl font-bold text-green-800">
                ${monthlyRent.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Key Metrics Summary */}
          {metrics && (
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="text-center p-3 bg-gray-50 rounded-lg border">
                <div className="text-xl font-bold text-purple-600">
                  {metrics.capRate?.toFixed(2)}%
                </div>
                <div className="text-xs text-gray-600 mt-1">Cap Rate</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg border">
                <div className="text-xl font-bold text-purple-600">
                  ${metrics.monthlyCashFlow?.toLocaleString()}
                </div>
                <div className="text-xs text-gray-600 mt-1">Monthly Cash Flow</div>
              </div>
            </div>
          )}

          {/* Quick Assessment Badges */}
          {metrics && (
            <div className="flex flex-wrap gap-2 mb-6">
              {metrics.capRate >= 8 ? (
                <Badge className="bg-green-100 text-green-700 border border-green-200">Strong Cap Rate</Badge>
              ) : metrics.capRate >= 5 ? (
                <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200">Moderate Cap Rate</Badge>
              ) : (
                <Badge className="bg-red-100 text-red-700 border border-red-200">Low Cap Rate</Badge>
              )}
              
              {metrics.monthlyCashFlow > 0 ? (
                <Badge className="bg-green-100 text-green-700 border border-green-200">Positive Cash Flow</Badge>
              ) : (
                <Badge className="bg-red-100 text-red-700 border border-red-200">Negative Cash Flow</Badge>
              )}
            </div>
          )}

          {/* Analysis Section */}
          <div className="border-t pt-6">
            {!analysis ? (
              <div className="text-center py-6">
                <div className="mb-4">
                  <Sparkles className="w-12 h-12 mx-auto text-purple-400 mb-3" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Get Expert Investment Analysis
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    Our AI will analyze this deal as a real estate investment expert, 
                    comparing your metrics to industry standards and providing 
                    actionable recommendations in plain English.
                  </p>
                </div>
                <Button
                  onClick={generateAnalysis}
                  disabled={isAnalyzing || !metrics}
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8"
                  data-testid="generate-ai-analysis-btn"
                >
                  {isAnalyzing ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing Deal...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Analyze This Investment
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    <span>Investment Analysis</span>
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={generateAnalysis}
                    disabled={isAnalyzing}
                  >
                    {isAnalyzing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      'Refresh Analysis'
                    )}
                  </Button>
                </div>
                
                <div className="bg-gray-50 p-5 rounded-lg border">
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
                      {analysis}
                    </pre>
                  </div>
                </div>

                {/* Disclaimer reminder at bottom */}
                <div className="flex items-start space-x-2 text-xs text-gray-500 bg-amber-50 p-3 rounded-lg border border-amber-100">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>
                    This AI-generated analysis is for educational purposes only and should not be 
                    considered financial advice. Always verify with qualified professionals.
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvestorAICoach;
